import { createContext, useContext, useState, type ReactNode } from "react";

export type StepVisibilityMap = Record<string, string>;

const StepVisibilityContext = createContext<{
  visibility: StepVisibilityMap | undefined;
  setVisibility: (v: StepVisibilityMap) => void;
}>({ visibility: undefined, setVisibility: () => {} });

const StepSlugContext = createContext<string | undefined>(undefined);
const DefaultsCalledContext = createContext(false);
const SetDefaultsCalledContext = createContext<(v: boolean) => void>(() => {});

export function StepVisibilityProvider({
  value,
  onChange,
  children
}: {
  value: StepVisibilityMap | undefined;
  onChange: (v: StepVisibilityMap) => void;
  children: ReactNode;
}) {
  return (
    <StepVisibilityContext.Provider value={{ visibility: value, setVisibility: onChange }}>
      {children}
    </StepVisibilityContext.Provider>
  );
}

export function StepSlugProvider({ slug, children }: { slug: string; children: ReactNode }) {
  return <StepSlugContext.Provider value={slug}>{children}</StepSlugContext.Provider>;
}

export function EventDefaultsProvider({ children }: { children: ReactNode }) {
  const [called, setCalled] = useState(false);
  return (
    <DefaultsCalledContext.Provider value={called}>
      <SetDefaultsCalledContext.Provider value={setCalled}>{children}</SetDefaultsCalledContext.Provider>
    </DefaultsCalledContext.Provider>
  );
}

export const useSetStepVisibility = () => useContext(StepVisibilityContext).setVisibility;
export const useHasEventDefaults = () => useContext(DefaultsCalledContext);
export const useSetDefaultsCalled = () => useContext(SetDefaultsCalledContext);

export function useStepVisibility() {
  const { visibility } = useContext(StepVisibilityContext);
  const slug = useContext(StepSlugContext);
  const status = slug ? visibility?.[slug] : undefined;
  return { disabled: status === "disabled", hidden: status === "hidden" };
}
